import axios from '@/components/axios'
import store from '@/store'
import {
  Inviter,
  Registerer,
  RegistererState,
  SessionState,
  UserAgent,
  Web
} from 'sip.js'

const API_BASE = process.env.VUE_APP_APIENDPOINT

function nowIso () {
  return new Date().toISOString()
}

function getTargetNumber (session) {
  try {
    return session.remoteIdentity.uri.user || ''
  } catch (e) {
    return ''
  }
}

function getTargetName (session) {
  try {
    return session.remoteIdentity.displayName || getTargetNumber(session)
  } catch (e) {
    return ''
  }
}

class ToneLooper {
  constructor (pattern) {
    this.pattern = pattern
    this.context = null
    this.timer = null
    this.step = 0
  }

  async start () {
    if (this.context || typeof window === 'undefined') return
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) return
    this.context = new AudioContextClass()
    if (this.context.state === 'suspended') {
      try {
        await this.context.resume()
      } catch (e) {
        return
      }
    }
    this.loop()
  }

  loop () {
    if (!this.context) return
    const current = this.pattern[this.step % this.pattern.length]
    this.step += 1
    if (current.play) {
      const oscillator = this.context.createOscillator()
      const gain = this.context.createGain()
      oscillator.type = 'sine'
      oscillator.frequency.value = current.frequency
      gain.gain.value = current.gain || 0.05
      oscillator.connect(gain)
      gain.connect(this.context.destination)
      oscillator.start()
      oscillator.stop(this.context.currentTime + (current.duration / 1000))
    }
    this.timer = window.setTimeout(() => this.loop(), current.duration)
  }

  stop () {
    if (this.timer) window.clearTimeout(this.timer)
    this.timer = null
    this.step = 0
    if (this.context) {
      this.context.close().catch(() => {})
    }
    this.context = null
  }
}

class PhoneService {
  constructor () {
    this.userAgent = null
    this.registerer = null
    this.activeSession = null
    this.pendingCallLogId = null
    this.incomingCallLogId = null
    this.runtimeSessionConfig = null
    this.remoteAudio = null
    this.localStream = null
    this.isBootstrapped = false
    this.isDisposing = false
    this.ringtone = new ToneLooper([
      { play: true, frequency: 440, duration: 350, gain: 0.04 },
      { play: false, duration: 120 },
      { play: true, frequency: 554, duration: 350, gain: 0.04 },
      { play: false, duration: 1200 }
    ])
    this.ringback = new ToneLooper([
      { play: true, frequency: 425, duration: 1000, gain: 0.025 },
      { play: false, duration: 4000 }
    ])
    this.beforeUnloadHandler = () => {
      this.dispose({ keepState: false })
    }
    this.onlineHandler = () => {
      if (store.getters['phone/selectedExtension']) {
        this.registerSelectedExtension().catch(() => {})
      }
    }
    this.offlineHandler = () => {
      store.commit('phone/SET_DISCONNECTED_REASON', 'Network connection lost.')
    }
  }

  ensureAudioElement () {
    if (this.remoteAudio) return
    this.remoteAudio = document.createElement('audio')
    this.remoteAudio.autoplay = true
    this.remoteAudio.playsInline = true
    this.remoteAudio.style.display = 'none'
    document.body.appendChild(this.remoteAudio)
  }

  async bootstrap () {
    if (!localStorage.getItem('accessToken')) return
    if (!navigator.mediaDevices || !window.RTCPeerConnection) {
      store.commit('phone/SET_UNSUPPORTED_ERROR', 'Your browser does not support WebRTC calling.')
      return
    }

    if (this.isBootstrapped) return
    store.commit('phone/SET_BOOTSTRAP_LOADING', true)
    store.commit('phone/SET_BOOTSTRAP_ERROR', '')

    try {
      const response = await axios({
        method: 'GET',
        url: `${API_BASE}/phone/bootstrap`
      })
      const payload = response.data.data || {}
      store.commit('phone/SET_EXTENSIONS', payload.extensions || [])
      store.commit('phone/SET_BOOTSTRAP_LOADED', true)
      this.isBootstrapped = true
      await this.loadDevices()
      window.addEventListener('beforeunload', this.beforeUnloadHandler)
      window.addEventListener('online', this.onlineHandler)
      window.addEventListener('offline', this.offlineHandler)
      if (store.state.phone.selectedExtensionId) {
        await this.registerSelectedExtension()
      }
    } catch (error) {
      store.commit('phone/SET_BOOTSTRAP_ERROR', error.response?.data?.message || 'Unable to load phone configuration.')
    } finally {
      store.commit('phone/SET_BOOTSTRAP_LOADING', false)
    }
  }

  async loadDevices () {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      store.commit('phone/SET_AVAILABLE_INPUT_DEVICES', devices.filter(item => item.kind === 'audioinput'))
      store.commit('phone/SET_AVAILABLE_OUTPUT_DEVICES', devices.filter(item => item.kind === 'audiooutput'))
      await store.dispatch('phone/persistPreferences')
    } catch (error) {
      store.commit('phone/SET_PERMISSION_ERROR', 'Unable to enumerate audio devices.')
    }
  }

  async requestMicrophone () {
    try {
      const audio = store.state.phone.selectedInputDeviceId ? { deviceId: { exact: store.state.phone.selectedInputDeviceId } } : true
      const constraints = {
        audio,
        video: false
      }
      this.localStream = await navigator.mediaDevices.getUserMedia(constraints)
      store.commit('phone/SET_PERMISSION_ERROR', '')
      return this.localStream
    } catch (error) {
      store.commit('phone/SET_PERMISSION_ERROR', 'Microphone permission was denied or no input device is available.')
      throw error
    }
  }

  async fetchSessionConfig () {
    const extensionId = store.state.phone.selectedExtensionId
    if (!extensionId) {
      throw new Error('Please select an extension first.')
    }
    const response = await axios({
      method: 'POST',
      url: `${API_BASE}/phone/extensions/${extensionId}/session`
    })
    const data = response.data.data
    this.runtimeSessionConfig = data
    store.commit('phone/SET_SESSION_CONFIG', {
      ...data,
      sip_password: undefined
    })
    await store.dispatch('phone/persistPreferences')
    return data
  }

  buildMediaConstraints (withVideo = false) {
    const audio = store.state.phone.selectedInputDeviceId ? { deviceId: { exact: store.state.phone.selectedInputDeviceId } } : true
    return {
      audio,
      video: withVideo
    }
  }

  async registerSelectedExtension () {
    const selected = store.getters['phone/selectedExtension']
    if (!selected) return

    const sessionConfig = this.runtimeSessionConfig || await this.fetchSessionConfig()
    if (this.registerer && this.userAgent) {
      await this.unregister()
    }

    const wsUrl = `${sessionConfig.transport || 'wss'}://${sessionConfig.ws_server}:${sessionConfig.ws_port}${sessionConfig.server_path || ''}`
    const uri = UserAgent.makeURI(`sip:${sessionConfig.sip_username}@${sessionConfig.sip_domain}`)
    if (!uri) {
      store.commit('phone/SET_REGISTRATION_ERROR', 'Invalid SIP URI configuration.')
      store.commit('phone/SET_REGISTRATION_STATE', 'error')
      return
    }

    store.commit('phone/SET_REGISTRATION_STATE', 'registering')
    store.commit('phone/SET_REGISTRATION_ERROR', '')
    this.ensureAudioElement()

    this.userAgent = new UserAgent({
      uri,
      authorizationUsername: sessionConfig.sip_username,
      authorizationPassword: sessionConfig.sip_password,
      displayName: sessionConfig.display_name,
      transportOptions: {
        server: wsUrl
      },
      delegate: {
        onInvite: invitation => this.handleIncomingInvite(invitation)
      },
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: {
          iceServers: (sessionConfig.stun_servers || []).map(url => ({ urls: url }))
        }
      },
      userAgentString: 'NG ERP Browser Phone'
    })

    this.userAgent.transport.stateChange.addListener(state => {
      if (String(state).toLowerCase().includes('disconnected')) {
        store.commit('phone/SET_DISCONNECTED_REASON', 'SIP transport disconnected.')
      }
    })

    await this.userAgent.start()
    this.registerer = new Registerer(this.userAgent, {
      expires: sessionConfig.register_expires || 300
    })
    this.registerer.stateChange.addListener(newState => {
      switch (newState) {
      case RegistererState.Registered:
        store.commit('phone/SET_REGISTRATION_STATE', 'registered')
        store.commit('phone/SET_DISCONNECTED_REASON', '')
        break
      case RegistererState.Unregistered:
        store.commit('phone/SET_REGISTRATION_STATE', 'unregistered')
        break
      case RegistererState.Terminated:
        store.commit('phone/SET_REGISTRATION_STATE', 'error')
        break
      default:
        break
      }
    })

    try {
      await this.registerer.register()
    } catch (error) {
      store.commit('phone/SET_REGISTRATION_STATE', 'error')
      store.commit('phone/SET_REGISTRATION_ERROR', error.message || 'SIP registration failed.')
      throw error
    }
  }

  async unregister () {
    try {
      if (this.registerer) {
        await this.registerer.unregister()
      }
    } catch (e) {
      // Ignore unregister errors during cleanup.
    }

    if (this.userAgent) {
      try {
        await this.userAgent.stop()
      } catch (e) {
        // Ignore stop errors during cleanup.
      }
    }

    this.registerer = null
    this.userAgent = null
  }

  async dial (number) {
    const targetNumber = String(number || store.state.phone.numberInput || '').trim()
    if (!targetNumber) return
    if (!this.userAgent || !store.getters['phone/isRegistered']) {
      await this.registerSelectedExtension()
    }

    await this.requestMicrophone()
    const config = this.runtimeSessionConfig || await this.fetchSessionConfig()
    const target = UserAgent.makeURI(`sip:${encodeURIComponent(targetNumber)}@${config.sip_domain}`)
    if (!target) {
      throw new Error('Invalid target number.')
    }

    const inviter = new Inviter(this.userAgent, target, {
      sessionDescriptionHandlerOptions: {
        constraints: this.buildMediaConstraints(false)
      }
    })

    this.activeSession = inviter
    store.commit('phone/SET_DIALER_VISIBLE', true)
    store.commit('phone/SET_LAST_DIALED_NUMBER', targetNumber)
    store.commit('phone/SET_NUMBER_INPUT', targetNumber)
    store.commit('phone/SET_ACTIVE_CALL', {
      direction: 'outgoing',
      remoteNumber: targetNumber,
      remoteName: targetNumber,
      status: 'ringing',
      statusLabel: 'Calling...',
      startedAt: nowIso(),
      durationSeconds: 0,
      callLogId: null
    })
    this.ringback.start()

    const callLog = await this.createCallLog({
      phone_extension_id: store.state.phone.selectedExtensionId,
      direction: 'outgoing',
      status: 'ringing',
      call_medium: 'audio',
      remote_number: targetNumber,
      remote_name: targetNumber,
      started_at: nowIso(),
      sip_call_id: inviter.id,
      meta: {
        source: 'browser-phone'
      }
    })
    this.pendingCallLogId = callLog?.id || null
    store.commit('phone/UPDATE_ACTIVE_CALL', { callLogId: this.pendingCallLogId })

    this.bindSession(inviter, 'outgoing')

    try {
      await inviter.invite()
    } catch (error) {
      this.ringback.stop()
      await this.updateCallLog(this.pendingCallLogId, {
        status: 'failed',
        ended_at: nowIso(),
        ended_by: 'system',
        hangup_reason: error.message || 'Dial failed'
      })
      store.commit('phone/UPDATE_ACTIVE_CALL', {
        status: 'failed',
        statusLabel: 'Call failed'
      })
      throw error
    }
  }

  async handleIncomingInvite (invitation) {
    const remoteNumber = getTargetNumber(invitation)
    const remoteName = getTargetName(invitation)

    if (store.state.phone.activeCall) {
      await invitation.reject()
      await this.createCallLog({
        phone_extension_id: store.state.phone.selectedExtensionId,
        direction: 'incoming',
        status: 'missed',
        call_medium: 'audio',
        remote_number: remoteNumber,
        remote_name: remoteName,
        started_at: nowIso(),
        ended_at: nowIso(),
        ended_by: 'system',
        hangup_reason: 'Busy'
      })
      return
    }

    this.activeSession = invitation
    this.incomingCallLogId = null
    store.commit('phone/SET_DIALER_VISIBLE', true)
    store.commit('phone/SET_INCOMING_CALL', {
      direction: 'incoming',
      remoteNumber,
      remoteName,
      startedAt: nowIso()
    })
    this.ringtone.start()

    const callLog = await this.createCallLog({
      phone_extension_id: store.state.phone.selectedExtensionId,
      direction: 'incoming',
      status: 'ringing',
      call_medium: 'audio',
      remote_number: remoteNumber,
      remote_name: remoteName,
      started_at: nowIso(),
      sip_call_id: invitation.id,
      meta: {
        source: 'browser-phone'
      }
    })
    this.incomingCallLogId = callLog?.id || null
    this.bindSession(invitation, 'incoming')
  }

  bindSession (session, direction) {
    session.stateChange.addListener(async newState => {
      if (newState === SessionState.Establishing) {
        return
      }

      if (newState === SessionState.Established) {
        this.ringtone.stop()
        this.ringback.stop()
        const remoteNumber = getTargetNumber(session)
        const remoteName = getTargetName(session)
        const startedAt = store.state.phone.activeCall?.startedAt || store.state.phone.incomingCall?.startedAt || nowIso()
        store.commit('phone/SET_INCOMING_CALL', null)
        store.commit('phone/SET_ACTIVE_CALL', {
          direction,
          remoteNumber,
          remoteName,
          status: 'answered',
          statusLabel: 'In call',
          startedAt,
          answeredAt: nowIso(),
          durationSeconds: 0,
          callLogId: direction === 'incoming' ? this.incomingCallLogId : this.pendingCallLogId
        })
        await this.updateCallLog(direction === 'incoming' ? this.incomingCallLogId : this.pendingCallLogId, {
          status: 'answered',
          answered_at: nowIso()
        })
        this.attachRemoteMedia(session)
        this.startDurationTicker()
      }

      if (newState === SessionState.Terminated) {
        const activeCall = store.state.phone.activeCall
        const callLogId = activeCall?.callLogId || this.pendingCallLogId || this.incomingCallLogId
        const answered = !!activeCall?.answeredAt
        const status = answered ? 'completed' : (direction === 'incoming' ? 'missed' : 'cancelled')

        this.ringtone.stop()
        this.ringback.stop()
        this.stopDurationTicker()
        await this.updateCallLog(callLogId, {
          status,
          ended_at: nowIso(),
          ended_by: answered ? 'remote' : 'local',
          duration_seconds: activeCall?.durationSeconds || 0
        })
        if (!this.isDisposing) {
          store.commit('phone/RESET_PHONE_STATE')
          store.commit('phone/SET_DIALER_VISIBLE', store.state.phone.dialerVisible)
        }
        this.activeSession = null
        this.pendingCallLogId = null
        this.incomingCallLogId = null
      }
    })
  }

  attachRemoteMedia (session) {
    this.ensureAudioElement()
    const peerConnection = session.sessionDescriptionHandler?.peerConnection
    if (!peerConnection) return

    const bindStream = () => {
      const remoteStream = new MediaStream()
      peerConnection.getReceivers().forEach(receiver => {
        if (receiver.track) remoteStream.addTrack(receiver.track)
      })
      if (remoteStream.getTracks().length) {
        this.remoteAudio.srcObject = remoteStream
        this.applyOutputDevice().catch(() => {})
      }
    }

    peerConnection.addEventListener('track', bindStream)
    bindStream()
  }

  startDurationTicker () {
    this.stopDurationTicker()
    this.durationTimer = window.setInterval(() => {
      const activeCall = store.state.phone.activeCall
      if (!activeCall?.answeredAt) return
      const seconds = Math.max(0, Math.floor((Date.now() - new Date(activeCall.answeredAt).getTime()) / 1000))
      store.commit('phone/UPDATE_ACTIVE_CALL', { durationSeconds: seconds })
    }, 1000)
  }

  stopDurationTicker () {
    if (this.durationTimer) window.clearInterval(this.durationTimer)
    this.durationTimer = null
  }

  async answer () {
    if (!this.activeSession || !store.state.phone.incomingCall) return
    await this.requestMicrophone()
    this.ringtone.stop()
    await this.activeSession.accept({
      sessionDescriptionHandlerOptions: {
        constraints: this.buildMediaConstraints(false)
      }
    })
  }

  async reject () {
    if (!this.activeSession) return
    this.ringtone.stop()
    await this.activeSession.reject()
  }

  async end () {
    if (!this.activeSession) return
    const session = this.activeSession
    this.ringtone.stop()
    this.ringback.stop()
    if (session.state === SessionState.Initial || session.state === SessionState.Establishing) {
      await session.cancel()
    } else if (session.state === SessionState.Established) {
      await session.bye()
    } else if (session.state !== SessionState.Terminated) {
      await session.dispose()
    }
  }

  async toggleMute (forceValue = null) {
    const peerConnection = this.activeSession?.sessionDescriptionHandler?.peerConnection
    if (!peerConnection) return
    const nextValue = forceValue === null ? !store.state.phone.isMuted : !!forceValue
    peerConnection.getSenders().forEach(sender => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = !nextValue
      }
    })
    store.commit('phone/SET_IS_MUTED', nextValue)
  }

  async toggleHold (forceValue = null) {
    if (!this.activeSession || typeof this.activeSession.invite !== 'function') return
    const nextValue = forceValue === null ? !store.state.phone.isOnHold : !!forceValue
    await this.activeSession.invite({
      sessionDescriptionHandlerModifiers: nextValue ? [Web.holdModifier] : []
    })
    store.commit('phone/SET_IS_ON_HOLD', nextValue)
    store.commit('phone/UPDATE_ACTIVE_CALL', {
      statusLabel: nextValue ? 'On hold' : 'In call'
    })
  }

  sendDtmf (digit) {
    const peerConnection = this.activeSession?.sessionDescriptionHandler?.peerConnection
    if (!peerConnection) return
    const sender = peerConnection.getSenders().find(item => item.dtmf)
    if (sender?.dtmf) {
      sender.dtmf.insertDTMF(String(digit), 160, 70)
    }
  }

  async setSelectedExtension (extensionId) {
    store.commit('phone/SET_SELECTED_EXTENSION_ID', extensionId)
    await store.dispatch('phone/persistPreferences')
    if (store.getters['phone/hasActiveCall']) return
    await this.registerSelectedExtension()
  }

  async setInputDevice (deviceId) {
    store.commit('phone/SET_SELECTED_INPUT_DEVICE_ID', deviceId)
    await store.dispatch('phone/persistPreferences')
  }

  async setOutputDevice (deviceId) {
    store.commit('phone/SET_SELECTED_OUTPUT_DEVICE_ID', deviceId)
    await store.dispatch('phone/persistPreferences')
    await this.applyOutputDevice()
  }

  async applyOutputDevice () {
    if (!this.remoteAudio || !this.remoteAudio.setSinkId) return
    const deviceId = store.state.phone.selectedOutputDeviceId
    if (!deviceId) return
    try {
      await this.remoteAudio.setSinkId(deviceId)
    } catch (e) {
      // Unsupported or blocked by browser.
    }
  }

  async createCallLog (payload) {
    try {
      const response = await axios({
        method: 'POST',
        url: `${API_BASE}/phone/call-logs`,
        data: payload
      })
      const item = response.data.data || null
      if (item) {
        store.commit('phone/UPSERT_CALL_LOG', item)
      }
      return item
    } catch (error) {
      return null
    }
  }

  async updateCallLog (id, payload) {
    if (!id) return null
    try {
      const response = await axios({
        method: 'PATCH',
        url: `${API_BASE}/phone/call-logs/${id}`,
        data: payload
      })
      const item = response.data.data || null
      if (item) {
        store.commit('phone/UPSERT_CALL_LOG', item)
      }
      return item
    } catch (error) {
      return null
    }
  }

  async fetchCallLogs (params = {}) {
    store.commit('phone/SET_CALL_LOGS_LOADING', true)
    store.commit('phone/SET_CALL_LOGS_ERROR', '')
    try {
      const response = await axios({
        method: 'GET',
        url: `${API_BASE}/phone/call-logs`,
        params: {
          per_page: store.state.phone.callLogsPagination.per_page,
          ...params
        }
      })
      const data = response.data.data || {}
      store.commit('phone/SET_CALL_LOGS', data.items || [])
      store.commit('phone/SET_CALL_LOGS_PAGINATION', data.pagination || {})
    } catch (error) {
      store.commit('phone/SET_CALL_LOGS_ERROR', error.response?.data?.message || 'Unable to load call history.')
    } finally {
      store.commit('phone/SET_CALL_LOGS_LOADING', false)
    }
  }

  async dispose ({ keepState = true } = {}) {
    this.isDisposing = true
    this.stopDurationTicker()
    this.ringtone.stop()
    this.ringback.stop()
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop())
      this.localStream = null
    }
    if (this.activeSession && this.activeSession.state !== SessionState.Terminated) {
      try {
        if (this.activeSession.state === SessionState.Established) {
          await this.activeSession.bye()
        } else if (typeof this.activeSession.dispose === 'function') {
          await this.activeSession.dispose()
        }
      } catch (e) {
        // Ignore cleanup errors.
      }
    }
    this.activeSession = null
    await this.unregister()
    this.runtimeSessionConfig = null
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    window.removeEventListener('online', this.onlineHandler)
    window.removeEventListener('offline', this.offlineHandler)
    if (!keepState) {
      store.commit('phone/RESET_PHONE_STATE')
    }
    this.isBootstrapped = false
    this.isDisposing = false
  }
}

export const phoneService = new PhoneService()
